import properties from '@netx/core-app/properties/theme';

import config from '@netx/core-app/config';
import Netx from '@netx/core/bootstrap';
import {isPortal} from '@netx/core/lib/utils';

const customPropertyPrefix = config.CSS_CUSTOM_PROPERTY_PREFIX;
const THEME_MODES = Object.keys(properties.modes);

// Will store all css properties here
let currentProperties = {};
// We can detect the browser mode once portal supports dark mode (mainly having a dark mode logo)
let currentThemeMode = config.DEFAULT_THEME_MODE; //window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : config.DEFAULT_THEME_MODE;
let fetchedCSS = false;
let userPrefsReady = false;

const documentElement = document.documentElement;
const style = documentElement.style;

/**
 * @function
 * @param {string} key Style property key
 * @param {string} value Style property value
 */
const setStyleProperty = (key, value) => {
	if (value) {
		return style.setProperty(key, value);
	}

	// Look for default value
	const defaultValue =
		config.CSS_CUSTOM_PROPERTIES[
			`${currentThemeMode}-${key.replace(customPropertyPrefix, '')}`
		];

	defaultValue
		? style.setProperty(key, defaultValue)
		: style.removeProperty(key);
};

/**
 * Get the current theme mode
 * @function
 * @return {string} Theme mode
 */
Netx.getThemeMode = () => {
	// No `dark` support for portal currently
	if (isPortal()) return 'light';
	// First try user prefs then default
	return userPrefsReady
		? Netx.getPreference?.(properties.userPref) || currentThemeMode
		: currentThemeMode;
};

/**
 * Get style property from DOM
 * @function
 * @return {string} Style property
 */
Netx.getStyleProperty = (key) => {
	const style = getComputedStyle(document.body);
	return style.getPropertyValue(`${customPropertyPrefix}${key}`).trim();
};

/**
 * @function
 * @returns string | undefined
 */
Netx.getLoginBackgroundUrl = () => {
	return !isPortal()
		? `${Netx.URL.loginBackground}?cb=${
				currentProperties.updated || Date.now()
		  }`
		: undefined;
};

/**
 * Changes address bar color in supported browsers
 * @function
 */
Netx.setMetaThemeColor = (value) => {
	const meta = document.querySelector('meta[name=theme-color]');
	meta && meta.setAttribute('content', value);
};

/**
 * Set style property on DOM
 * @function
 * @param {string} key Style property key
 * @param {string} value Style property value
 */
Netx.setStyleProperty = (key, value) => {
	currentProperties[key] = value;

	// We use this for cachebusting the background image since last update
	if (key === 'updated') return false;

	key = key.replace(`${currentThemeMode}-`, '');

	if (key === 'primary') {
		Netx.setMetaThemeColor(value);
	}
	setStyleProperty(`${customPropertyPrefix}${key}`, value);
	return true;
};

/**
 * @function
 * @param {string} name Theme mode name
 * @fires module:netx#theme:toggled
 */
Netx.setThemeMode = (name) => {
	if (!THEME_MODES.includes(name)) {
		name = config.DEFAULT_THEME_MODE;
	}

	currentThemeMode = name;

	const keys = Object.keys(currentProperties).filter((key) =>
		key.includes(`${currentThemeMode}-`),
	);
	keys.forEach((key) => {
		Netx.setStyleProperty(key, currentProperties[key]);
	});

	// Clear theme-mode classes
	THEME_MODES.forEach((mode) =>
		documentElement.classList.remove(`theme-${mode}`),
	);
	// Add selected theme-mode class
	document.documentElement.classList.add(`theme-${currentThemeMode}`);
	// Add selected theme mode meta
	Netx.setMetaThemeColor(currentThemeMode);

	// Cache bust since last update
	// DAM-20791: there is a bug in chromium that causes background images, set via css variables,
	// to do a fresh reload on clicks/style changes (the only other workaraound atm is to changes
	// the cache policy on the sys ops side)
	// !isPortal() &&
	// 	Netx.setStyleProperty(
	// 		'login-background-image',
	// 		`url(${Netx.getLoginBackgroundUrl()})`,
	// 	);

	// Dispatch event for the rest of the app to listen to
	dispatcher.trigger('theme:toggled', name);
};

/**
 * When user prefs are available we can set the theme mode to the user's selection
 * @event module:app#app:user:prefs:ready
 */
// TODO: these `once` dispatchers on the global scope of a file are hard to test from multiple ways
// having a hard time testing the the initialize thoroughly and this having not `fetchedCSS`
dispatcher.once('app:user:prefs:ready', () => {
	userPrefsReady = true;
	if (!fetchedCSS) return;

	Netx.setThemeMode?.(Netx.getThemeMode());
});

const initialize = () => {
	// Set the default theme mode class
	documentElement.classList.add(`theme-${currentThemeMode}`);

	// Just go ahead and set from the getgo
	// NOTE: we do not currently support this for portals
	// DAM-20791: there is a bug in chromium that causes background images, set via css variables,
	// to do a fresh reload on clicks/style changes (the only other workaraound atm is to changes
	// the cache policy on the sys ops side)
	// !isPortal() &&
	// 	Netx.setStyleProperty(
	// 		'login-background-image',
	// 		`url(${Netx.URL.loginBackground}?cb=${Date.now()})`,
	// 	);

	// Load css properties from server
	return new Promise((resolve, reject) => {
		$.ajax({
			cache: false,
			dataType: 'json',
			type: 'GET',
			url: Netx.URL.customCssProperties,
		}).then(
			(data) => {
				Object.assign(
					data,
					_.pick.apply(
						_,
						[config.CSS_CUSTOM_PROPERTIES].concat(
							_.difference(
								Object.keys(config.CSS_CUSTOM_PROPERTIES),
								Object.keys(data),
							),
						),
					),
				);

				// Store all data
				currentProperties = data;
				// Set current theme (will loop over data and set based on theme mode)
				Netx.setThemeMode(Netx.getThemeMode());

				fetchedCSS = true;

				console.timeEnd('loadcustomcss');
				resolve();
			},
			(jqXHR, status, errorThrown) => {
				// What to do?
				reject(errorThrown);
			},
		);
	});
};

export default function () {
	return initialize();
}
